import { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { X, ArrowCircleUp, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function DepositPanel({ controlledOpen, onOpenChange, hideTrigger = false }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = (v) => { onOpenChange ? onOpenChange(v) : setInternalOpen(v); };
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/deposit/info");
      setInfo(r.data);
    } catch {
      setInfo({ configured: false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const copy = async (text, label) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <>
      {!hideTrigger && (
        <Button
          data-testid="open-deposit-btn"
          onClick={() => setOpen(true)}
          variant="outline"
          className="rounded-sm border-[#1F2937] hover:bg-[#1F2937] text-[#9CA3AF] hover:text-white h-9 px-3"
        >
          <ArrowCircleUp size={16} className="sm:mr-2" />
          <span className="hidden sm:inline">Deposit</span>
        </Button>
      )}

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          data-testid="deposit-panel-overlay"
        >
          <div
            className="bg-[#0A0E17] border border-[#1F2937] rounded-sm w-full max-w-md p-5 sm:p-6 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="deposit-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-5">
              <div>
                <p className="overline">Wallet</p>
                <h2 className="text-xl font-semibold mt-1">Deposit USDT</h2>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="close-deposit-btn"
                className="text-[#9CA3AF] hover:text-white"
              >
                <X size={20} />
              </button>
            </div>

            {/* Body */}
            <div className="space-y-4">
              {loading && (
                <div className="text-sm text-[#9CA3AF] text-center py-6">Loading…</div>
              )}

              {!loading && info && !info.configured && (
                <div
                  className="text-sm text-[#9CA3AF] bg-[#111827] border border-[#1F2937] rounded-sm p-4 text-center"
                  data-testid="deposit-not-configured"
                >
                  Deposit instructions have not been configured yet.<br />
                  Please contact the administrator.
                </div>
              )}

              {!loading && info && info.configured && (
                <>
                  {/* Network */}
                  <div>
                    <p className="overline mb-1.5">Network</p>
                    <div
                      onClick={() => copy(info.network, "Network")}
                      data-testid="deposit-network"
                      className="bg-[#111827] border border-[#1F2937] hover:border-[#0ECB81] rounded-sm px-3 py-2.5 flex items-center justify-between cursor-pointer group transition-colors"
                    >
                      <span className="mono text-sm text-white">{info.network}</span>
                      <Copy size={14} className="text-[#9CA3AF] group-hover:text-[#0ECB81]" />
                    </div>
                  </div>

                  {/* Wallet address */}
                  <div>
                    <p className="overline mb-1.5">Wallet address</p>
                    <div
                      onClick={() => copy(info.wallet, "Address")}
                      data-testid="deposit-wallet"
                      className="bg-[#111827] border border-[#1F2937] hover:border-[#0ECB81] rounded-sm px-3 py-2.5 flex items-center justify-between cursor-pointer group transition-colors"
                    >
                      <span className="mono text-xs sm:text-sm text-white break-all pr-2">{info.wallet}</span>
                      <Copy size={14} className="text-[#9CA3AF] group-hover:text-[#0ECB81] shrink-0" />
                    </div>
                    <p className="text-[10px] text-[#9CA3AF] mt-1.5">Tap to copy address.</p>
                  </div>

                  {/* Note */}
                  {info.note && (
                    <div data-testid="deposit-note">
                      <p className="overline mb-1.5">Note</p>
                      <div className="bg-[#111827] border border-[#1F2937] rounded-sm px-3 py-2.5 text-sm text-white whitespace-pre-wrap">
                        {info.note}
                      </div>
                    </div>
                  )}

                  {/* Contact */}
                  {info.contact && (
                    <div data-testid="deposit-contact">
                      <p className="overline mb-1.5">Contact admin</p>
                      <div className="bg-[#111827] border border-[#1F2937] rounded-sm px-3 py-2.5 text-sm text-[#FCD535] break-all">
                        {info.contact}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
